// Copyright (c) 2017 Comsearch, Inc., All rights reserved

import { fromJS } from 'immutable'

import {
  castToFloat,
  castToString,
} from 'helpers/reduxFormFunctions'

// The functions which return initial values guarantee that
// the initial values can be based on state (user or project settings)
// and to avoid any pass-by-reference that could arise over multiple forms on a single session.

// hard coded regulatory values are specific to domainId 1
export const makeInitialSiteValues = (unitType, company, contact) => ({
  areaOfOperation: '',
  azimuth: '-',
  certifiedFlag13Ghz: true,
  companyId: castToFloat(company?.companyId),
  companyName: castToString(company?.companyName),
  contactId: castToFloat(contact?.contactId),
  county: '',
  elevation: '',
  elevationUnit: unitType,
  latitude: '',
  longitude: '',
  passiveFlag: false, // this is never modified, simply passed along to API
  radiusKm: '',
  radiusKmUnit: unitType,
  segmentEndId: null,
  serviceContactCode: castToString(contact?.contactCode),
  siteName: '',
})

// eg. [site, site]
export const makeSetOfInitialSiteValues = (unitType, company, contact) => ([
  makeInitialSiteValues(unitType, company, contact),
  makeInitialSiteValues(unitType, company, contact),
])

export const initialRegulatoryValues = {
  asr: '',
  callSign: '',
  licenseBasisId: '1',
  radioServiceId: '',
  stationClassId: '2',
}

const setOfInitialRegulatoryValues = radioServiceId => (
  Array.from({ length: 2 }).map(() => ({
    ...initialRegulatoryValues,
    radioServiceId: castToString(radioServiceId) || '',
  }))
)

export const initialAdministrationValues = {
  expirationDate: '',
  licenseDate: '',
  protectFlag: false,
  publicNoticeDate: '',
  segmentStatusId: '3',
  ulsFileNumber: '',
}

const setOfInitialAdministrationValues = (
  Array(2).fill().map(() => ({ ...initialAdministrationValues }))
)

/**
 * @typedef {Object} SegmentEndAntenna
 * @property {string} antennaCode
 * @property {Number} antennaSpecId
 * @property {'Primary' | 'Diversity'} antennaType
 * @property {string} beamwidth
 * @property {string} centerline
 * @property {'SI' | 'US'} centerlineUnit
 * @property {string} classification
 * @property {string} company
 * @property {string} gain
 * @property {'Tx only' | 'Tx/Rx' | 'Rx only'} mode
 * @property {string} modelNumber
 * @property {Number} segmentEndAntennaId
 * @property {string} tilt
 */

/**
 *
 * @param {'SI' | 'US'} unitType
 * @param {'Primary' | 'Diversity'} antennaType
 * @returns {SegmentEndAntenna}
 */
export const makeInitialAntennaValues = (unitType, antennaType = 'Primary') => ({
  antennaCode: '',
  antennaSpecId: null,
  antennaType, // Primary || Diversity
  beamwidth: '',
  centerline: '',
  centerlineUnit: unitType,
  classification: '',
  company: '',
  gain: '',
  mode: antennaType === 'Primary' ? 'Tx/Rx' : 'Rx only', // Main Antennas default mode is 'Tx/Rx' but Additional Antennas default is 'Rx only'
  modelNumber: '',
  segmentEndAntennaId: null,
  tilt: '',
})

// eg. [[antenna], [antenna]]
export const make2dSetOfInitialAntennas = unitType => (
  Array(2).fill().map(() => [makeInitialAntennaValues(unitType)])
)

export const initialBackToBackValues = {
  commonLoss: '',
  polarizationChange: 'no',
}

// passes unitType down to the site and antenna
export const makeInitialPassiveValues = unitType => ({
  antennas: [makeInitialAntennaValues(unitType)],
  backToBack: [],
  site: {
    asr: null,
    callSign: null,
    companyId: null,
    contactId: null,
    elevation: '',
    elevationUnit: unitType,
    latitude: '',
    longitude: '',
    passiveFlag: true, // this is never modified, simply passed along to API
    passiveRepeaterType: 'billboard',
    siteName: '',
    segmentStatusId: '3',
    stateId: '',
  },
})

/**
 * @typedef {Object} SegmentEndRadio
 * @property {boolean} acmCoordinationAdvantageFlag
 * @property {string} architecture
 * @property {null} atpc
 * @property {boolean} atpcEnabled
 * @property {string} bandwidth - cast as number
 * @property {string} companyName
 * @property {string} coordinatedPower - cast as number
 * @property {boolean} dynamicPowerFlag
 * @property {Number} engineeringReference - points to the modulation's id
 * @property {String} legacyRadioCode
 * @property {string} maxPower - cast as number
 * @property {string} modelNumber
 * @property {Object[]} modulations - TODO add this typedef
 * @property {string} modulationType - TODO - this should use modulationTypeId and not the string!
 * @property {string} powerType - TODO - look into this one?
 * @property {string} radioCode
 * @property {string} radioFamilyName
 * @property {Number} radioFavoriteId
 * @property {Number} radioId
 */

/**
 * @type SegmentEndRadio
 */
export const initialRadioValues = {
  acmCoordinationAdvantageFlag: false,
  architecture: '',
  atpc: null,
  atpcEnabled: false,
  bandwidth: '',
  companyName: '',
  coordinatedPower: '',
  dynamicPowerFlag: false,
  engineeringReference: null, // the modulation's id
  legacyRadioCode: null,
  maxPower: '',
  modelNumber: '',
  modulations: [],
  modulationType: '',
  powerType: '',
  radioCode: '',
  radioFamilyName: '',
  radioFavoriteId: null,
  radioId: null,
}

/**
 * @typedef {Object} SegmentEndLoss
 * @property {string} commonLoss - cast as number
 * @property {string} rxTotalLoss - cast as number
 * @property {string} txTotalLoss - cast as number
 */

/**
 * @type SegmentEndLoss
 */
export const initialLossValues = {
  commonLoss: '',
  rxTotalLoss: '',
  txTotalLoss: '',
}

/**
 * @typedef {Object} SegmentEndFrequency
 * @property {string} channelFrequency - cast as number
 * @property {Number} channelFrequencyId
 * @property {Number} channelNumber
 * @property {'' | '1' | '2' | '3' | '4' | '5' | '6'} polarizationId
 * @property {Number} segmentEndFrequencyId
 * @property {'channelNumber'} uniqueId
 */

/**
 * @type SegmentEndFrequency
 */
export const initialFrequencyValues = {
  channelFrequency: '',
  channelFrequencyId: null,
  channelNumber: null,
  polarizationId: '',
  segmentEndFrequencyId: null,
  uniqueId: 'channelNumber', // for <DataTable /> to display the frequencies
}

/**
 * 13 frequencies with their own channelNumber for UI ,starting at 1
 * @type {SegmentEndFrequency[]}
 */
export const initialFrequenciesValues = (
  Array.apply(null, Array(13)).reduce((a, d, i) => {
    a.push({
      ...initialFrequencyValues,
      channelNumber: i + 1,
    })
    return a
  }, [])
)

/**
 * @typedef {Object} HiLoSummary
 * @property {Number} hi
 * @property {Number} lo
 */

/**
 * @type HiLoSummary
 */
const hl = {
  hi: null,
  lo: null,
}

/**
 * @type {{environmentHiLoSummary: HiLoSummary, projectHiLoSummary: HiLoSummary}}
 */
export const initialHiLoSummaries = {
  environmentHiLoSummary: hl,
  projectHiLoSummary: hl,
}

/**
 * @typedef {Object} SegmentEndFrequencyPlan
 * @property {HiLoSummary} environmentHiLoSummary
 * @property {HiLoSummary} projectHiLoSummary
 * @property {boolean} customFrequencyPlan
 * @property {SegmentEndFrequency[]} frequencies
 * @property {string} frequencyPlan
 * @property {Number} frequencyPlanId
 * @property {string} frequencyPlanStored
 * @property {'' | 'H' | 'L'} pairDesignation
 */

/**
 * @type SegmentEndFrequencyPlan
 */
export const initialFrequencyPlanValues = {
  environmentHiLoSummary: hl,
  projectHiLoSummary: hl,
  customFrequencyPlan: false,
  frequencies: initialFrequenciesValues,
  frequencyPlan: '',
  frequencyPlanId: null,
  frequencyPlanStored: '',
  pairDesignation: '',
}

/**
 * @typedef {Object} PathCalculationValues
 * @property {boolean} atpcEnabled
 * @property {string} rslEirpMessage
 * @property {*[]} modulationsCalculations - TODO - give this definition
 * @property {null} radioLevelCalculations - TODO give this definition
 * @property {string} siteName
 */

/**
 * @type [PathCalculationValues,PathCalculationValues]
 */
export const initialPathCalculationValues = (
  Array(2).fill().map(() => ({
    atpcEnabled: false,
    rslEirpMessage: '',
    modulationsCalculations: [],
    radioLevelCalculations: null,
    siteName: '',
  }))
)

export const makeInitialFormValues = config => {
  const {
    company,
    contact,
    domainId,
    pathStatusId,
    radioServiceId,
    unitType,
  } = config

  return {
    administration: setOfInitialAdministrationValues,
    antennas: make2dSetOfInitialAntennas(unitType),
    band: '',
    createInfo: {},
    deleted: false,
    domainId,
    frequencyPlans: [initialFrequencyPlanValues, initialFrequencyPlanValues],
    isSimplex: false,
    jobCode: '',
    ebandLinkConstructionDate: '',
    ebandLinkRegistrationDate: '',
    ebandLinkRegistrationNumber: '',
    ebandLinkRegistrationStatus: '',
    lastModifiedInfo: {},
    losses: [initialLossValues, initialLossValues],
    passiveRepeaters: [makeInitialPassiveValues(unitType)],
    pathCalculations: initialPathCalculationValues,
    pathDistance: '-',
    pathId: null,
    pathName: '',
    pathStatusId,
    pplFlippedFlag: null,
    pplPathId: null,
    pplRcn: null,
    pplSite1Name: null,
    pplSite2Name: null,
    radios: [initialRadioValues, initialRadioValues],
    rcnNumber: '',
    rcnStatusCode: '',
    regulatory: setOfInitialRegulatoryValues(radioServiceId),
    segmentIds: [],
    sites: makeSetOfInitialSiteValues(unitType, company, contact),
    unitType,
  }
}

export const initialState = fromJS({
  comments: [],
  deleted: false, // tracks if the path has been soft deleted or not.
  defaultCompany: null,
  defaultContact: null,
  error: '',
  modalMessage: '',
  modalMeta: '', // used to pass arguments to the modal for discard and deletion
  modalTemplate: '',
  nextPathId: null, // used fro path details footer next button
  passiveRepeatersStore: [],
  pathId: null,
  pathIndex: '',
  projectId: null,
  prevPathId: null, // used for path details footer prev button
  showPassiveRepeaters: false,
  sourceForm: null,
  unitType: '', // project default if w/in project summary, otherwise user default
  userCanEdit: false,
  withinBulkEdit: false, // used to support Bulk Edit needs where necessary
  withinProjectSummary: false, // used to determine routing actions and some display changes
})
